import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Autocomplete,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { showInsufficientCreditsModal } from "../actions/creditActions";
import { deductCredits } from "../utils/HelperFunction/deductCredits";
import debounce from "lodash/debounce";
import {
  sendInvitationApi,
  sendInvitationExistingUserApi,
  searchUsersApi,
} from "../services/api/caseManagementApi";

const AddNewLawyerDialog = ({ open, onClose, data }) => {
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const [loading, setLoading] = useState(false);
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);
  const [emailInput, setEmailInput] = useState("");
  const [options, setOptions] = useState([]);

  const isUserOwnEmail = emailInput === userInfo?.email;

  const isExistingUser = useMemo(() => {
    if (!emailInput) return false;
    return options.some((user) => user.email === emailInput);
  }, [emailInput, options]);

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      email: data?.email || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Email is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (isExistingUser) {
          const user = options?.find((u) => u.email === emailInput);
          const method = "POST";
          const payload = {
            senderId: userId,
            inviteeId: user._id,
            type: "lawyer",
          };
          await saveLawyerApi(payload, method);
        } else {
          await sendInvitation(userId, emailInput);
        }
      } catch (error) {
        console.error("Error saving lawyer:", error);
      } finally {
        setLoading(false);
        onClose();
        formik.resetForm();
      }
    },
  });

  const sendInvitation = async (senderId, email) => {
    try {
      const response = await sendInvitationApi(senderId, email, "lawyer");

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = response.data;
      toast.success("Invitation sent successfully");
      return data;
    } catch (error) {
      console.error("Error sending invitation:", error);
      throw error;
    }
  };

  const saveLawyerApi = async (caseData, method) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }
    const response = await sendInvitationExistingUserApi(caseData);
    if (response.status < 200 || response.status >= 300) {
      const errorData = response?.data;
      const errorText = errorData || "Failed to save lawyer";
      toast.error(errorText);
      throw new Error(errorText);
    }
    const successText = "Invitation sent successfully";
    toast.success(successText);
    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: `${
          method === "PUT"
            ? "Edit Lawyer on Case Management"
            : "Add new Lawyer on Case Management"
        }`,
      },
      dispatch
    );
    if (!deductionSuccess) {
      console.warn("Failed to deduct credits. Ensure retry or user notification.");
    }
    return response.data;
  };

  const searchUser = async (input) => {
    try {
      const response = await searchUsersApi(input);
      const data = response.data;

      if (Array.isArray(data) && data.length > 0) {
        setOptions(data.filter((obj) => obj.email !== userInfo?.email));
      } else {
        setOptions([]);
      }
    } catch (error) {
      console.error("Error searching user:", error);
      setOptions([]);
    }
  };

  const debouncedFetchData = useCallback(
    debounce((input) => {
      searchUser(input);
    }, 500),
    []
  );

  useEffect(() => {
    if (emailInput.trim() !== "" && emailInput.length > 2) {
      debouncedFetchData(emailInput);
    }
  }, [emailInput, debouncedFetchData]);

  const handleEmailChange = (e, newValue) => {
    const email =
      typeof newValue === "string" ? newValue : newValue?.email || "";
    formik.setFieldValue("email", email);
    setEmailInput(email);
  };

  const handleModalClose = () => {
    formik.resetForm();
    formik.setErrors({});
    formik.setFieldValue({});
    formik.setValues({});
    onClose();
  };

  return (
    <Dialog
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          boxShadow: 24,
          p: 1,
          width: 400,
        },
      }}
      open={open}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          fontSize: "18px",
          fontWeight: "bold",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        Invite a New Lawyer
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Autocomplete
              freeSolo
              size="small"
              options={options?.filter((option) => option.email)}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.email
              }
              inputValue={emailInput}
              onInputChange={(e, newValue) => {
                setEmailInput(newValue);
                formik.setFieldValue("email", newValue);
              }}
              value={formik.values.email}
              onChange={handleEmailChange}
              isOptionEqualToValue={(option, value) => {
                if (typeof value === "string") {
                  return option.email === value;
                }
                return option.email === value?.email;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    fontSize: "14px",
                    "& .MuiInputBase-input": { fontSize: "14px" },
                  }}
                  label="Email"
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  InputLabelProps={{ sx: { fontSize: "14px" } }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "13px",
                      maxHeight: "120px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": { width: "4px" },
                      "::-webkit-scrollbar-track": { background: "#fff" },
                      "::-webkit-scrollbar-thumb": { background: "#bbb", borderRadius: "4px" },
                    },
                  },
                },
              }}
            />
            {isUserOwnEmail && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "background.default",
                }}
              >
                <span>💡</span>
                <Typography variant="body2">
                  That's your own email. No need to invite yourself.
                </Typography>
              </Box>
            )}
            {options?.length === 0 && emailInput && !isUserOwnEmail && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "background.default",
                }}
              >
                <span>💡</span>
                <Typography variant="body2">
                  The email entered is not associated with a registered LawGPT
                  account. Would you like to send an invite?
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              onClose();
              formik.resetForm();
            }}
            sx={{
              borderRadius: "24px",
              padding: "8px 24px",
              textTransform: "none",
              fontWeight: "bold",
            }}
            variant="outlined"
            color="error"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={() => {
              formik.handleSubmit();
            }}
            sx={{
              borderRadius: "24px",
              padding: "8px 24px",
              textTransform: "none",
              fontWeight: "bold",
              backgroundColor: "#003D78",
              "&:hover": { backgroundColor: "primary.main", color: "#fff" },
              transition: "background-color 0.3s ease-in-out",
            }}
            disabled={loading || isUserOwnEmail}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <CircularProgress size={18} sx={{ color: "#a9a9a9" }} />
                <Box> Inviting...</Box>
              </Box>
            ) : (
              "Invite Lawyer"
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddNewLawyerDialog;
